<template>
  <Modal
    size="xl"
    @close="$emit('close')"
  >
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('HeadContract.DelaysSchedule.DelayShowModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full">
        <div
          v-if="fetchDelayAction.is(States.COMPLETE) && delay"
          class="flex flex-col space-y-4 items-center w-full relative"
        >
          <div class="grid grid-cols-[1fr_auto_1fr] w-full">
            <div>
              <template
                v-if="projectsStore.project?.unarchived"
              >
                <button
                  v-if="delay.status === DelayStatus.approved"
                  type="button"
                  class="btn btn--secondary-blue"
                  @click="completeDelay"
                >
                  {{ $t('HeadContract.DelaysSchedule.DelayShowModal.button.markAsComplete') }}
                </button>

                <button
                  v-if="delay.status < DelayStatus.approved"
                  type="button"
                  class="btn btn--secondary-alert"
                  @click="declineDelay"
                >
                  {{ $t('HeadContract.DelaysSchedule.DelayShowModal.button.markAsDeclined') }}
                </button>
              </template>
            </div>

            <div
              :style="{ backgroundColor: delay.statusColour, color: delay.statusTextColour }"
              class="py-2 px-4 rounded-lg"
            >
              {{ delay.statusTitle }}
            </div>

            <div class="ml-auto">
              <button
                type="button"
                class="btn btn--secondary-blue"
                @click="showDetails = !showDetails"
              >
                {{ showDetails ? $t('HeadContract.DelaysSchedule.DelayShowModal.button.viewAttachments') : $t('HeadContract.DelaysSchedule.DelayShowModal.button.viewDetails') }}
              </button>
            </div>
          </div>


          <div
            v-if="showDetails"
            class="flex space-x-4 w-full"
          >
            <div class="flex flex-col w-1/2 space-y-2">
              <div
                v-if="delay.contract_event"
                class="p-2 rounded-lg bg-blue-50 border border-blue-200"
              >
                <div class="flex items-start space-x-1 font-semibold text-sm text-blue-900">
                  <RouterLink
                    v-if="userStore.hasAccessToRoute('CenSchedule')"
                    class="btn btn-icon--small btn-icon-blue"
                    :to="{ name: 'CenSchedule', params: { projectId: project.id, cenId: delay.contract_event.id } }"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
                  </RouterLink>

                  <div>
                    <div class="flex items-start space-x-1">
                      <span>{{ $t('HeadContract.DelaysSchedule.DelayShowModal.raisedFromContractEvent') }}</span>

                      <span class="font-semibold">{{ delay.contract_event.code }}</span>
                    </div>

                    <p>
                      {{ delay.contract_event.name }}
                    </p>
                  </div>
                </div>
              </div>

              <div
                v-if="delay.variation_order"
                class="p-2 rounded-lg bg-blue-50 border border-blue-200"
              >
                <div class="flex items-start space-x-1 font-semibold text-sm text-blue-900">
                  <RouterLink
                    v-if="userStore.hasAccessToRoute('VariationsSchedule')"
                    class="btn btn-icon--small btn-icon-blue"
                    :to="{ name: 'VariationsSchedule', params: { projectId: project.id, variationId: delay.variation_order.id } }"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
                  </RouterLink>

                  <div>
                    <div class="flex items-start space-x-1">
                      <span>{{ $t('HeadContract.DelaysSchedule.DelayShowModal.attachedToVariation') }}</span>

                      <span class="font-semibold">{{ delay.variation_order.code }}</span>
                    </div>

                    <p>
                      {{ delay.variation_order.description }}
                    </p>
                  </div>
                </div>
              </div>

              <p class="font-semibold text-lg">
                {{ $t('HeadContract.DelaysSchedule.DelayShowModal.delayDetails') }}
              </p>

              <form class="flex flex-col space-y-4">
                <label>
                  {{ $t('HeadContract.DelaysSchedule.DelayShowModal.label.description') }} <span class="font-semibold">{{ delay.code }}</span>

                  <AppInput
                    v-model="delayForm.form.description"
                    type="text"
                    name="description"
                    class="form-input"
                    :error="delayForm.getErrors('description')"
                  />
                </label>

                <div>
                  {{ project?.isContractor ? $t('HeadContract.DelaysSchedule.DelayShowModal.label.clientReference') : $t('HeadContract.DelaysSchedule.DelayShowModal.label.contractorReference') }}

                  <AppInput
                    v-model="delayForm.form.client_ref"
                    type="text"
                    name="client_ref"
                    class="form-input"
                    :error="delayForm.getErrors('client_ref')"
                  />
                </div>

                <label v-if="!delay.date_of_occurrence">
                  {{ $t('HeadContract.DelaysSchedule.DelayShowModal.label.dateOfOccurrence') }}

                  <AppInput
                    v-model="delayForm.form.date_of_occurrence"
                    :error="delayForm.getErrors('date_of_occurrence')"
                    class="form-input"
                    type="date"
                    name="date_of_occurrence"
                  />
                </label>

                <label v-if="delay.status < DelayStatus.provisioned">
                  Start Date

                  <AppInput
                    v-model="delayForm.form.start_date"
                    :error="delayForm.getErrors('start_date')"
                    class="form-input"
                    type="date"
                    name="start_date"
                    :disabled="delay.status >= DelayStatus.submitted"
                    :title="delay.status >= DelayStatus.submitted ? 'Cannot be adjusted while submitted to client' : ''"
                  />
                </label>

                <label v-if="delay.status < DelayStatus.provisioned">
                  End Date

                  <AppInput
                    v-model="delayForm.form.end_date"
                    :error="delayForm.getErrors('end_date')"
                    class="form-input"
                    type="date"
                    name="end_date"
                    :disabled="delay.status >= DelayStatus.submitted"
                    :title="delay.status >= DelayStatus.submitted ? 'Cannot be adjusted while submitted to client' : ''"
                  />
                </label>

                <label>
                  {{ $t('HeadContract.DelaysSchedule.DelayShowModal.label.notes') }}

                  <textarea
                    v-model="delayForm.form.notes"
                    class="resize-none form-input"
                  />

                  <p
                    v-if="delayForm.hasErrors('notes')"
                    class="text-sm text-red-600"
                  >
                    {{ delayForm.getErrors('notes') }}
                  </p>
                </label>
              </form>
            </div>

            <div class="w-1/2 flex flex-col">
              <p class="font-semibold text-lg">
                {{ $t('HeadContract.DelaysSchedule.DelayShowModal.statusTimeline') }}
              </p>

              <div class="mt-2">
                <div class="flex justify-between">
                  <span>
                    {{ $t('HeadContract.DelaysSchedule.DelayShowModal.dateOccurred') }}
                  </span>

                  <span
                    v-if="delay.dateOfOccurrence"
                    class="font-semibold"
                  >{{ delay.dateOfOccurrence.toLocaleString() }}</span>

                  <span
                    v-else
                    class="font-semibold"
                  >{{ $t('HeadContract.DelaysSchedule.DelayShowModal.notSubmitted') }}</span>
                </div>

                <div
                  v-if="delay.status >= DelayStatus.provisioned && delay.startDate"
                  class="flex justify-between"
                >
                  <span>
                    Start Date
                  </span>

                  <span class="font-semibold">{{ delay.startDate.toLocaleString() }}</span>
                </div>

                <div
                  v-if="delay.status >= DelayStatus.provisioned && delay.endDate"
                  class="flex justify-between"
                >
                  <span>
                    End Date
                  </span>

                  <span class="font-semibold">{{ delay.endDate.toLocaleString() }}</span>
                </div>

                <template v-if="delay.contract_event">
                  <div class="flex justify-between">
                    <span>
                      {{ $t('HeadContract.DelaysSchedule.DelayShowModal.raisedBy', { name: delay.contract_event.user?.name }) }}
                    </span>

                    <span class="font-semibold">{{ delay.contract_event.createdAt.toLocaleString() }}</span>
                  </div>

                  <div class="flex justify-between">
                    <span>
                      {{ $t('HeadContract.DelaysSchedule.DelayShowModal.registeredBy', { name: delay.user?.name }) }}
                    </span>

                    <span class="font-semibold">{{ delay.createdAt.toLocaleString() }}</span>
                  </div>
                </template>

                <div
                  v-else
                  class="flex justify-between"
                >
                  <span>
                    {{ $t('HeadContract.DelaysSchedule.DelayShowModal.raisedBy', { name: delay.user?.name }) }}
                  </span>

                  <span class="font-semibold">{{ delay.createdAt.toLocaleString() }}</span>
                </div>

                <div
                  v-if="delay.registeredAt && project.isContractor"
                  class="flex justify-between"
                >
                  <span>
                    {{ $t('HeadContract.DelaysSchedule.DelayShowModal.accepted') }}
                  </span>

                  <span class="font-semibold">{{ delay.registeredAt.toLocaleString() }}</span>
                </div>

                <div
                  v-if="noticeDueDate && !delay.notice_sent_at && project?.head_contract_settings?.nod_required"
                  class="flex justify-between rounded-md"
                  :class="noticeClass(noticeDueIn)"
                >
                  <span>
                    {{ $t('HeadContract.DelaysSchedule.DelayShowModal.dateToBeIssuedBy') }}
                  </span>

                  <span class="font-semibold">{{ noticeDueDate.toLocaleString() }}</span>
                </div>

                <div
                  v-if="delay.noticeSentAt"
                  class="flex justify-between"
                >
                  <span>
                    {{ project.isClient ? $t('HeadContract.DelaysSchedule.DelayShowModal.noticeRegistered') : $t('HeadContract.DelaysSchedule.DelayShowModal.noticeSent') }}
                  </span>

                  <span class="font-semibold">{{ delay.noticeSentAt.toLocaleString() }}</span>
                </div>

                <div
                  v-if="delay.submittedAt && project.isContractor"
                  class="flex justify-between"
                >
                  <span>
                    {{ $t('HeadContract.DelaysSchedule.DelayShowModal.pricingSubmitted') }}
                  </span>

                  <span class="font-semibold">{{ delay.submittedAt.toLocaleString() }}</span>
                </div>

                <div
                  v-if="delay.submittedAt && project.isContractor"
                  class="flex justify-between"
                >
                  <span>
                    {{ $t('HeadContract.DelaysSchedule.DelayShowModal.durationSubmitted') }}
                  </span>

                  <span class="font-semibold">{{ delay.submittedAt.toLocaleString() }}</span>
                </div>

                <div
                  v-if="delay.provisionedAt"
                  class="flex justify-between"
                >
                  <span>
                    {{ project.isClient ? $t('HeadContract.DelaysSchedule.DelayShowModal.pricingReceived') : $t('HeadContract.DelaysSchedule.DelayShowModal.pricingApproved') }}
                  </span>

                  <span class="font-semibold">{{ delay.provisionedAt.toLocaleString() }}</span>
                </div>

                <div
                  v-if="delay.approvedAt"
                  class="flex justify-between"
                >
                  <span>
                    {{ $t('HeadContract.DelaysSchedule.DelayShowModal.fullyApproved') }}
                  </span>

                  <span class="font-semibold">{{ delay.approvedAt.toLocaleString() }}</span>
                </div>
              </div>

              <hr class="border-gray-300 my-2">

              <div class="flex justify-between">
                <span class="font-semibold">
                  {{ $t('HeadContract.DelaysSchedule.DelayShowModal.delayDuration') }}
                </span>

                <div>
                  {{ delay.days ? delay.days : $t('HeadContract.DelaysSchedule.DelayShowModal.notSet') }}
                </div>
              </div>
            </div>
          </div>

          <div
            v-else
          >
            <div
              v-if="delayMedia.mediaCollection.value.length === 0"
              class="font-semibold mb-4"
            >
              {{ $t('HeadContract.DelaysSchedule.DelayShowModal.noAttachmentsFound') }}
            </div>

            <FileUpload
              v-if="projectsStore.project?.unarchived"
              v-model="fileUploadForm"
              multiple
              @change="fileUploaded"
            />

            <button
              v-if="delayMedia.mediaCollection.value.length > 1"
              class="absolute left-0 inset-y-1/2 border border-black rounded-full w-8 h-8 flex justify-center items-center hover:bg-gray-100 active:bg-gray-200 z-10 bg-white"
              type="button"
              @click="mediaSwiper.slidePrev()"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <Swiper
              class="max-w-md mb-10"
              :space-between="20"
              :loop="delayMedia.mediaCollection.value?.length > 1"
              centered-slides
              :modules="[Pagination]"
              :pagination="{
                el: '.swiper-pagination',
                type: 'bullets'
              }"
              @swiper="setSwiper"
            >
              <SwiperSlide
                v-for="media in delayMedia.mediaCollection.value"
                :key="media.uuid"
                class="!h-auto"
              >
                <div class="flex items-center h-full w-full justify-center">
                  <MediaDisplay
                    class="max-w-60"
                    :media="media"
                    @delete="delayMedia.removeMedia(media.uuid)"
                    @refresh="delayMedia.refreshMedia(media.uuid)"
                  >
                    <p class="text-sm">
                      {{ $t('HeadContract.DelaysSchedule.DelayShowModal.attachToEmails') }}
                    </p>

                    <div class="flex text-sm space-x-2">
                      <div
                        v-if="project.isContractor"
                        class="flex space-x-1 items-center"
                      >
                        <p>
                          {{ $t('HeadContract.DelaysSchedule.DelayShowModal.label.notice') }}
                        </p>

                        <input
                          v-model="media.custom_properties.notice"
                          type="checkbox"
                          name="notice"
                          class="form-checkbox"
                          @change="delayMedia.updateSettings(media.uuid, { notice: ($event.target as any).checked })"
                        >
                      </div>

                      <div
                        v-if="project.isContractor"
                        class="flex space-x-1 items-center"
                      >
                        <p>
                          {{ $t('HeadContract.DelaysSchedule.DelayShowModal.label.submission') }}
                        </p>

                        <input
                          v-model="media.custom_properties.submission"
                          type="checkbox"
                          name="submission"
                          class="form-checkbox"
                          @change="delayMedia.updateSettings(media.uuid, { submission: ($event.target as any).checked })"
                        >
                      </div>

                      <div class="flex space-x-1 items-center">
                        <p>
                          {{ $t('HeadContract.DelaysSchedule.DelayShowModal.label.approval') }}
                        </p>

                        <input
                          v-model="media.custom_properties.approval"
                          type="checkbox"
                          name="approval"
                          class="form-checkbox"
                          @change="delayMedia.updateSettings(media.uuid, { approval: ($event.target as any).checked })"
                        >
                      </div>
                    </div>
                  </MediaDisplay>
                </div>
              </SwiperSlide>
            </Swiper>

            <div class="swiper-pagination" />

            <button
              v-if="delayMedia.mediaCollection.value.length > 1"
              class="absolute right-0 inset-y-1/2 border border-black rounded-full w-8 h-8 flex justify-center items-center hover:bg-gray-100 active:bg-gray-200 z-10 bg-white"
              type="button"
              @click="mediaSwiper.slideNext()"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>
          </div>
        </div>

        <div v-else-if="fetchDelayAction.is(States.LOADING)">
          <div class="flex justify-center">
            <AppSpinner />
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-between w-full">
        <button
          type="button"
          class="btn btn--tertiary-blue"
          @click="deleteDelay"
        >
          {{ $t('HeadContract.DelaysSchedule.DelayShowModal.button.delete') }}
        </button>

        <div class="w-full flex justify-end p-2 space-x-4">
          <button
            v-if="projectsStore.project?.unarchived && !delay.variation_order && delay.status !== DelayStatus.declined && delay.status !== DelayStatus.completed"
            type="button"
            class="btn btn--tertiary-blue"
            @click="$emit('attachVariation', delayId)"
          >
            {{ $t('HeadContract.DelaysSchedule.DelayShowModal.button.attachVariation') }}
          </button>

          <button
            type="button"
            class="btn btn--secondary-blue w-1/4"
            @click="$emit('close')"
          >
            {{ $t('HeadContract.DelaysSchedule.DelayShowModal.button.close') }}
          </button>

          <button
            v-if="projectsStore.project?.unarchived"
            type="button"
            class="btn btn--primary-blue w-1/4"
            @click="updateDelay"
          >
            {{ $t('HeadContract.DelaysSchedule.DelayShowModal.button.update') }}
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, dateForDateInput, enumKeyFromValue } from '@/helpers';
import Delay, { DelayStatus, DelayStatusColours } from '@/models/Delay';
import { useDelaysStore } from '@/store/delays';
import Swal from 'sweetalert2';
import { Pagination } from 'swiper/modules';
import { Swiper as SwiperInterface } from 'swiper/types';
import { computed, ref, watch } from 'vue';
import 'swiper/css/pagination';
import FileUpload from '@/components/FileUpload.vue';
import MediaDisplay from '@/components/MediaDisplay.vue';
import { useFileUploadForm } from '@/composables/useFileUploadForm';
import { useMediaCollection } from '@/composables/useMediaCollection';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { RouterLink } from 'vue-router';

const props = defineProps<{
  delayId: Id;
}>();

const emits = defineEmits<{
  (e: 'close');
  (e: 'attachVariation', id: Id);
}>();

const delaysStore = useDelaysStore();
const fetchDelayAction = useStoreApiAction(delaysStore.fetchDelay);

watch(() => props.delayId, (newValue) => {
  if(newValue) {
    fetchDelayAction.request(newValue).catch(console.error);
  }
}, { immediate: true });

const delay = computed(() => {
  return delaysStore.models
    .with('user')
    .with('variation_order')
    .with('contract_event', (query) => query.with('user'))
    .where('id', parseInt(props.delayId as string))
    .first();
});

const updateDelayOrderAction = useStoreApiAction(delaysStore.updateDelay);
const completeDelayAction = useStoreApiAction(delaysStore.completeDelay);
const declineDelayAction = useStoreApiAction(delaysStore.declineDelay);

const delayForm = useFormData({
  client_ref: '',
  notes: '',
  description: '',
  date_of_occurrence: '',
  start_date: '',
  end_date: '',
});

watch(delay, (newValue) => {
  if(newValue) {
    delayForm.setData(newValue, ['date_of_occurrence', 'start_date', 'end_date']);

    delayForm.form.date_of_occurrence = dateForDateInput(newValue.date_of_occurrence ?? new Date());
    delayForm.form.start_date = dateForDateInput(newValue.start_date);
    delayForm.form.end_date = dateForDateInput(newValue.end_date);
  }
});

const updateDelay = () => {
  updateDelayOrderAction.request(props.delayId, delayForm.form).then(() => {
    Swal.fire({
      icon: 'success',
      text: 'Delay Updated',
    });
  }).catch(console.error);
};

const completeDelay = () => {
  Swal.fire({
    title: 'Mark Delay as Complete?',
    icon: 'question',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      completeDelayAction.request(delay.value.id).then(() => {
        Swal.fire({
          icon: 'success',
          text: 'Delay Completed',
        }).then(() => {
          emits('close');
        });
      }).catch((error) => {
        console.log(error);
      });
    }
  });
};

const declineDelay = () => {
  Swal.fire({
    title: 'Mark delay as declined?',
    text: delay.value.description,
    icon: 'question',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      declineDelayAction.request(delay.value.id).then(() => {
        Swal.fire({
          icon: 'success',
          text: 'Delay marked as declined.',
        }).then(() => {
          emits('close');
        });
      }).catch((error) => {
        console.log(error);
      });
    }
  });
};

const delayMedia = useMediaCollection(delaysStore.media);

watch(() => props.delayId, (newValue) => {
  if(newValue) {
    delayMedia.fetchMedia(newValue);
  }
}, { immediate: true });

const showDetails = ref(true);
const mediaSwiper = ref<SwiperInterface>();

const setSwiper = (swiper: SwiperInterface) => {
  mediaSwiper.value = swiper;
};

const storeMediaAction = useStoreApiAction(delaysStore.storeMedia);
const fileUploadForm = ref();

const fileUploaded = (event: Event) => {
  const files = Array.from((event.target as HTMLInputElement).files);

  files.forEach((file) => {
    const fileFormData = new FormData();

    fileFormData.append('images[]', file);

    storeMediaAction.request(delay.value.id, fileFormData).then((medias) => {
      delayMedia.addMedia(medias);
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to upload attachments',
          text: error.data.message,
        });
      }
    });
  });
};

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});

const noticeDueDate = computed(() => {
  return delay.value?.dateOfOccurrence?.plus({ days: project.value?.head_contract_settings?.nod_period ?? 0 });
});

const noticeDueIn = computed(() => {
  if(noticeDueDate.value) {
    return Math.ceil(noticeDueDate.value.diffNow('days').days);
  }

  return 0;
});

const noticeClass = (dueIn: number) => {
  if(dueIn < 10 && dueIn > 5) {
    return 'bg-orange-300 border-orange-400 p-1';
  } else if(dueIn < 5) {
    return 'bg-red-300 border-red-400 p-1';
  }

  return '';
};

const deleteDelayAction = useStoreApiAction(delaysStore.deleteDelay);

const deleteDelay = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete Delay?',
    text: 'Are you sure you want to delete this delay? This action cannot be undone.',
    showConfirmButton: true,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      deleteDelayAction.request(delay.value.id).then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Delay Deleted.',
        });
      });
    }
  }).catch((error) => {
    if(error?.status === 403) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to delete delay.',
        text: error?.data?.message,
      });
    } else if(error?.status === 422) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to delete delay.',
        text: error?.data?.message,
      });
    }
  });
};

const userStore = useUserStore();
</script>

<style scoped></style>
